import { cn } from "@/lib/utils";

/**
 * A *value* tinted by how it measured up against a benchmark.
 *
 * The sibling of {@link DeltaCell}: where DeltaCell shows the **difference**
 * (▲/▼ + magnitude), BenchmarkCell shows the **number itself** and uses colour
 * alone to say whether it's good news. Reach for it in dense grids — monthly
 * actual-vs-budget, quota attainment, forecast vs plan — where the reader wants
 * the real figure and the arrow column would be noise.
 *
 *   invert=false  over the benchmark is GOOD  (revenue, bookings, attainment)
 *   invert=true   over the benchmark is BAD   (spend, cost per unit, churn)
 *
 * Colour is *only* an accent here, never the sole carrier of meaning — the
 * number and its benchmark are both available to a screen reader via the
 * generated `aria-label`, and callers should keep a plain budget row nearby.
 *
 * `pending` is the important escape hatch: a period that hasn't finished can't
 * be judged against a full-period benchmark (six days into the month, every
 * line looks gloriously under budget). Pass it and the cell renders untinted.
 */
export function BenchmarkCell({
  value,
  benchmark,
  invert = false,
  pending = false,
  threshold = 1,
  format = defaultFmt,
  emptyLabel = "—",
  onClick,
  actionHint,
  title,
  label,
  className,
}: {
  /** The number to display. Null/undefined renders `emptyLabel`, untinted. */
  value: number | null | undefined;
  /** What it's measured against. Null/undefined = nothing to compare, untinted. */
  benchmark: number | null | undefined;
  /** True when exceeding the benchmark is bad news (spend-like metrics). */
  invert?: boolean;
  /** The period isn't complete, so the comparison isn't meaningful yet. */
  pending?: boolean;
  /** Gap below which the two read as "on plan" (untinted). Default $1. */
  threshold?: number;
  /** Formatter for the displayed value. Defaults to compact currency. */
  format?: (n: number) => string;
  /** Rendered when `value` is null. Default em-dash. */
  emptyLabel?: React.ReactNode;
  /** Makes the cell an inline button (dotted underline) — e.g. drill to detail. */
  onClick?: () => void;
  /** Appended to the tooltip when clickable, e.g. "click for GL lines". */
  actionHint?: string;
  /** Native tooltip. Replaces the auto-generated over/under summary. */
  title?: string;
  /** Noun for the screen-reader label, e.g. "August actual". */
  label?: string;
  className?: string;
}) {
  if (value == null) {
    return <span className={cn("text-muted-foreground", className)}>{emptyLabel}</span>;
  }

  const comparable = !pending && benchmark != null && Math.abs(value - benchmark) >= threshold;
  const over = comparable && value > (benchmark as number);
  const good = invert ? !over : over;

  const tone = !comparable
    ? "text-foreground"
    : good
      ? "text-success"
      : "text-danger";

  const body = format(value);
  const summary = title ?? describe(value, benchmark, pending, comparable, over, format);
  const tip = [summary, onClick && actionHint].filter(Boolean).join(" · ") || undefined;
  const aria = label ? `${label}: ${body}${summary ? ` — ${summary}` : ""}` : undefined;

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={tip}
        aria-label={aria}
        className={cn(
          "tabular-nums underline decoration-dotted underline-offset-2 hover:decoration-solid",
          tone,
          className,
        )}
      >
        {body}
      </button>
    );
  }

  return (
    <span title={summary} aria-label={aria} className={cn("tabular-nums", tone, className)}>
      {body}
    </span>
  );
}

/** Plain-language tooltip: how far off plan, and in which direction. */
function describe(
  value: number,
  benchmark: number | null | undefined,
  pending: boolean,
  comparable: boolean,
  over: boolean,
  format: (n: number) => string,
): string | undefined {
  if (pending) return "Period still in progress — not yet comparable";
  if (benchmark == null) return undefined;
  if (benchmark === 0) return over ? "No budget for this period" : undefined;
  if (!comparable) return `On plan against ${format(benchmark)}`;
  return `${format(Math.abs(value - benchmark))} ${over ? "over" : "under"} ${format(benchmark)}`;
}

/** Compact currency, matching DeltaCell's default so the two agree in a grid. */
function defaultFmt(v: number): string {
  if (v === 0) return "—";
  if (Math.abs(v) >= 1_000_000) return "$" + (v / 1_000_000).toFixed(2) + "M";
  if (Math.abs(v) >= 10_000) return "$" + (v / 1_000).toFixed(1) + "K";
  return "$" + v.toLocaleString("en-US", { maximumFractionDigits: 0 });
}
